import React, { useMemo, useRef, useState, useEffect } from "react";
import moment from "moment-timezone";
import { useDocument } from "./loan_tool";
import { useUser } from "../../hooks/user";
import DOCUMENT_TYPES from "../../constants/document_types";
import { toFnmText } from "../../fnm/fnm";
import { toMismoXml } from "../../mismo/mismo";

export default function ExportMenu() {
  const [loanDoc, , { documentType, name }] = useDocument();
  const [{ accountProfile, companyProfile }, { getProfile }] = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // "fnm" | "mismo" | null
  const containerRef = useRef(null);
  const fnmDownloadRef = useRef(null);
  const mismoDownloadRef = useRef(null);

  useEffect(() => {
    getProfile();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fnmContent = useMemo(() => {
    try {
      return toFnmText(loanDoc, documentType, accountProfile, companyProfile);
    } catch (e) {
      console.error(e);
      return "";
    }
  }, [loanDoc]);

  const mismoContent = useMemo(() => {
    try {
      return toMismoXml(loanDoc, {
        documentType,
        encoding: "UTF-8",
        accountProfile,
        companyProfile
      });
    } catch (e) {
      console.error(e);
      return "";
    }
  }, [loanDoc, accountProfile, companyProfile]);

  const fnmDownloadUrl = useMemo(() => {
    if (!fnmContent) return null;
    return URL.createObjectURL(new Blob([fnmContent], { type: "text/plain" }));
  }, [fnmContent]);

  const mismoDownloadUrl = useMemo(() => {
    if (!mismoContent) return null;
    return URL.createObjectURL(new Blob([mismoContent], { type: "application/xml" }));
  }, [mismoContent]);

  const dateStr = moment().format("YYYY-MM-DD");
  const docLabel = DOCUMENT_TYPES[documentType];

  function selectMismo() {
    setIsOpen(false);
    setDialog("mismo");
  }

  function selectFnm() {
    setIsOpen(false);
    setDialog("fnm");
  }

  function confirmMismo() {
    setDialog(null);
    mismoDownloadRef.current.click();
  }

  function confirmFnm() {
    setDialog(null);
    fnmDownloadRef.current.click();
  }

  // ---- Demo-only: export EVERY loan option (one file per loan) ----------------
  // Standard subscribers never see this (gated by accountProfile.is_demo). It
  // does not touch the single-loan export above; it re-runs the same exporter
  // once per loan with that loan selected, and downloads each file.
  const isDemoAccount = !!(accountProfile && accountProfile.is_demo);

  function downloadFile(content, filename, mime) {
    const url = URL.createObjectURL(new Blob([content], { type: mime }));
    const a = window.document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  function exportAll(format) {
    setIsOpen(false);
    setDialog(null);
    const loans = (loanDoc && loanDoc.loans) || [];
    let fileIndex = 0;
    loans.forEach((loan, i) => {
      // Skip empty/unconfigured loan slots.
      if (!loan || (!loan.type && !loan.rate)) {
        return;
      }
      // Export this loan by selecting it, without mutating the real document.
      const docForLoan = {
        ...loanDoc,
        summary: { ...loanDoc.summary, selection_loan_number: "" + (i + 1) }
      };
      let content = "";
      let ext = "";
      let mime = "";
      try {
        if (format === "mismo") {
          content = toMismoXml(docForLoan, {
            documentType,
            encoding: "UTF-8",
            accountProfile,
            companyProfile
          });
          ext = "xml";
          mime = "application/xml";
        } else {
          content = toFnmText(docForLoan, documentType, accountProfile, companyProfile);
          ext = "FNM";
          mime = "text/plain";
        }
      } catch (e) {
        console.error("export-all loan " + (i + 1), e);
        return;
      }
      if (!content) return;
      // Stagger downloads so the browser doesn't drop them.
      const delay = fileIndex * 400;
      fileIndex += 1;
      setTimeout(
        () =>
          downloadFile(
            content,
            `${docLabel} - ${name} - Loan ${i + 1} - ${dateStr}.${ext}`,
            mime
          ),
        delay
      );
    });
  }

  return (
    <>
      <div className="forms-menu" ref={containerRef} style={{ marginLeft: "10px" }}>
        <button className="button" onClick={() => setIsOpen(!isOpen)}>
          Export File
        </button>
        {isOpen && (
          <ul className="forms-menu-dropdown">
            <li>
              <button className="forms-menu-item" onClick={selectMismo}>
                Fannie Mae MISMO 3.4 Export
              </button>
            </li>
            <li>
              <button className="forms-menu-item" onClick={selectFnm}>
                Fannie Mae DU / DU 3.2 Export
              </button>
            </li>
            {isDemoAccount && (
              <>
                <li
                  style={{
                    padding: "6px 12px",
                    fontSize: "11px",
                    color: "#8a6d3b",
                    borderTop: "1px solid #eee",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}
                >
                  Demo only — export ALL loans
                </li>
                <li>
                  <button
                    className="forms-menu-item"
                    onClick={() => exportAll("mismo")}
                  >
                    Export ALL Loans (MISMO 3.4)
                  </button>
                </li>
                <li>
                  <button
                    className="forms-menu-item"
                    onClick={() => exportAll("fnm")}
                  >
                    Export ALL Loans (DU 3.2)
                  </button>
                </li>
              </>
            )}
          </ul>
        )}
      </div>

      {/* Hidden download anchors */}
      {fnmDownloadUrl && (
        <a
          ref={fnmDownloadRef}
          href={fnmDownloadUrl}
          download={`${docLabel} - ${name} - ${dateStr}.FNM`}
          style={{ display: "none" }}
        />
      )}
      {mismoDownloadUrl && (
        <a
          ref={mismoDownloadRef}
          href={mismoDownloadUrl}
          download={`${docLabel} - ${name} - ${dateStr}.xml`}
          style={{ display: "none" }}
        />
      )}

      {/* MISMO confirmation dialog */}
      {dialog === "mismo" && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: "6px", padding: "30px",
            maxWidth: "480px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}>
            <p style={{ marginBottom: "24px", lineHeight: "1.5" }}>
              MISMO 3.4 does not map the closing costs listed on the Closing
              Cost Est or the Loan Estimate. You will need to print the Loan
              Estimate for the preferred loan the Borrower choses and then
              populate the Loan Origination Software (LOS) you are using.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button className="button" onClick={confirmMismo}>Yes, I understand</button>
              <button className="button" onClick={() => setDialog(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* FNM confirmation dialog */}
      {dialog === "fnm" && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: "6px", padding: "30px",
            maxWidth: "480px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}>
            <p style={{ marginBottom: "24px", lineHeight: "1.5" }}>
              Fannie Mae 3.2 does not map the closing costs listed on the
              Closing Cost Est or the Loan Estimate. You will need to print
              the Loan Estimate for the preferred loan the Borrower choses
              and then populate the Loan Origination Software (LOS) you are
              using.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button className="button" onClick={confirmFnm}>Yes, I understand</button>
              <button className="button" onClick={() => setDialog(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
